import React, { memo, useState } from "react";
import { compose } from "redux";
import { reduxForm } from "redux-form";
import { connect, useDispatch, useSelector } from "react-redux";
import { createStructuredSelector } from "reselect";

import {
  makeSelectAuthId,
  makeSelectThemeColors,
  makeSelectSocialMediaDetails,
  makeSelectLoginFormMessageData
} from "ducks/selectors";
import SmartRender from "containers/SmartRender";
import ResendActivationCode from "./ResendActivationCode";
import { getDeviceId } from "utils/localStorage";
import { getURLParams, isMobile, urlParam } from "utils/helpers";
import { forms } from "utils/constants";
import useAuthorizedUserRedirection from "hooks/useAuthorizedUserRedirection";
import authenticateUserAndSaveToken from "services/userAuthenticate";
import useLoadingSpinner from "hooks/useLoadingSpinner";
import { makeSelectIsValidOTP } from "ducks/selectors/forms/userLoginOTPForm";
import get from "lodash/get";
import isArray from "lodash/isArray";

const OTPSubmitPage = Component =>
  function Comp(props) {
    const dispatch = useDispatch();
    const redirectUser = useAuthorizedUserRedirection();
    const [isLoading, setisLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const { LoadingSpinner } = useLoadingSpinner();
    const { isSocialLogin } = getURLParams();
    const timerKey = urlParam("key") || props.timerKey;
    const [clearOTP, setClearOTP] = useState(false);
    const auth_id = useSelector(makeSelectAuthId());
    const themeColors = useSelector(makeSelectThemeColors());
    const socialMediaDetails = useSelector(makeSelectSocialMediaDetails());
    const isValidOTP = useSelector(makeSelectIsValidOTP());
    const loginFormMessageData = useSelector(makeSelectLoginFormMessageData());

    const combinedMessageData = {
      ...props.messageData,
      ...loginFormMessageData
    };

    const privateComponents = {
      RESEND_ACTIVATION_CODE_LINK: props => (
        <ResendActivationCode
          {...props}
          setErrorMessage={setErrorMessage}
          messageData={combinedMessageData}
        />
      )
    };

    /**
     * Submit a login request with user entered OTP and auth_id response
     * On success, navigates to correct landing page
     * @param {string} otp_code user provided OTP
     */
    const loginWithOTP = (_, payload) => {
      if (!isValidOTP) return;
      const additionalHeaders = {};
      const current_otp = get(payload, "current_otp");
      const otp_code = isArray(current_otp)
        ? current_otp.join("")
        : current_otp;
      const bodyParams = {
        device: {
          // TODO: Should this be dynamic?
          app_type: "ecosystem",
          device_id: getDeviceId(),
          device_type: isMobile ? "Mobile" : "web"
        },
        email_auth: {
          otp_code,
          auth_id
        }
      };

      if (isSocialLogin) {
        bodyParams.social_auth = socialMediaDetails;
      }

      /**
       * Triggers user routing upon successful authentication
       * @param {object} authResponse success response result
       * @param {object} errors API errors only if failed
       */
      const onUserAuthenticateCallback = (authResponse, errors) => {
        // setting to fall only when there are errors. No need for other case because user gets redirected
        if (errors) {
          setErrorMessage(authResponse.message);
          return setisLoading(false);
        } else redirectUser(authResponse, errors);
      };

      setClearOTP(true);
      setisLoading(true);
      authenticateUserAndSaveToken(
        additionalHeaders,
        bodyParams,
        onUserAuthenticateCallback,
        dispatch
      );
    };

    if (isLoading) return <LoadingSpinner />;

    return (
      <Component
        {...props}
        privateComponents={privateComponents}
        clearData={clearOTP}
        timerKey={timerKey}
        onSubmit={loginWithOTP}
        themeColors={themeColors}
        nobox
        errorMessage={errorMessage}
        isValidOTP={isValidOTP}
        messageData={combinedMessageData}
      />
    );
  };

const mapStateToProps = createStructuredSelector({
  themeColors: makeSelectThemeColors()
});
const mapDispatchToProps = {};
const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(
  withConnect,
  reduxForm({
    form: forms.USER_LOGIN_OTP_FORM,
    destroyOnUnmount: false
  }),
  memo
)(OTPSubmitPage(SmartRender));
